<template>
  <div class="endpoint_head">
    <h2>Create appointment</h2>

    <div class="route">
      <i
        @click="
          $router.push('/documentation/api/appointments#create');
          $toast.info('Set URL to endpoint');
        "
        :style="{ color: 'var(--clr-secondary)', cursor: 'pointer' }"
        class="fas fa-hashtag"
      ></i>
      <p class="tag tag-blue">POST</p>
      <p class="url">{{ apiUrl }}<span>/appointments</span></p>
    </div>

    <div class="description">
      <p>Creates an appointment</p>
    </div>
  </div>

  <div class="endpoint_body">
    <div class="request">
      <h3>Request</h3>

      <!-- Body -->
      <div class="endpoint_body_section path">
        <div class="head">
          <h4>Body</h4>
          <p>application/json</p>
        </div>

        <ul>
          <!-- Salon ID -->
          <li>
            <div class="col value">
              <p class="bold">salonId</p>
              <p class="description">String/ID of the salon</p>
              <p class="examples">
                Examples: <span>1b9d6bcd-bbfd-4b2d-9b5d-ab8dfbbd4bed</span>
              </p>
            </div>

            <div class="col">
              <p class="required">required</p>
            </div>
          </li>

          <!-- Date -->
          <li>
            <div class="col value">
              <p class="bold">date</p>
              <p class="description">Date of appointment</p>
              <p class="examples">
                Examples: <span>2024-01-01T02:17:32.375+00:00</span>
              </p>
            </div>

            <div class="col">
              <p class="required">required</p>
            </div>
          </li>

          <!-- Services -->
          <li>
            <div class="col value">
              <p class="bold">services</p>
              <p class="description">Array of services on appointment</p>
              <p class="examples">
                Examples:
                <span
                  >[ { start: "14:00", service: {ServiceObject}, duration: "20",
                  staff: {StaffMinObject}, requested: false } ]</span
                >
              </p>
            </div>

            <div class="col">
              <p class="required">required</p>
            </div>
          </li>

          <!-- Service Object -->
          <li class="subitem">
            <div class="col value">
              <p class="bold">{ServiceObject}</p>
              <p class="description">
                ** This is not a part of the request, this is an example of the
                service object sent in the "services" array
              </p>
              <p class="examples">
                Examples:
                <span
                  >{ "title": "Item", "color": "#a6c7ea", "isCommissioned":
                  "false", "price": 100, "taxRate": 0, "duration": 20,
                  "categoryId": "622ec883ec2552e0d48ed212" }</span
                >
              </p>
            </div>

            <div class="col"></div>
          </li>

          <!-- Staff Min Object -->
          <li class="subitem">
            <div class="col value">
              <p class="bold">{StaffMinObject}</p>
              <p class="description">
                ** This is not a part of the request, this is an example of the
                staff min object sent in the "services" array
              </p>
              <!-- Above sentence in -->
              <p class="examples">
                Examples:
                <span
                  >{ "staffId": "622ec119ec2552e0d48ed192", "firstName": "John",
                  "lastName": "Doe", "email": "john@doe.com", "phoneNumber":
                  "5551112222", "commission": 60 }</span
                >
              </p>
            </div>

            <div class="col"></div>
          </li>

          <!-- Title -->
          <li>
            <div class="col value">
              <p class="bold">title</p>
              <p class="description">
                Appointment title to display on calendar (client name overrides
                if client is attached to appointment)
              </p>
              <p class="examples">Examples: <span>Walk-in</span></p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>

          <!-- Note -->
          <li>
            <div class="col value">
              <p class="bold">note</p>
              <p class="description">Appointment note</p>
              <p class="examples">
                Examples: <span>Client has lorem ipsum</span>
              </p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>

          <!-- Client -->
          <li>
            <div class="col value">
              <p class="bold">client</p>
              <p class="description">
                Client associated with appointment (client must already exist)
              </p>
              <p class="examples">
                Examples:
                <span
                  >{ "_id": "6238b0baa691db3afc07e534", "firstName": "John",
                  "lastName": "Doe", "phoneNumber": "5551112222", "email":
                  "john@doe.com" }</span
                >
              </p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>

          <!-- Booked online -->
          <li>
            <div class="col value">
              <p class="bold">bookedOnline</p>
              <p class="description">
                Boolean - If set to "true" double book check will be enabled
                (Will throw error if booking over another appoimtment [can
                override with skipDoubleBookCheck])
              </p>
              <p class="examples">
                Examples:
                <span>true</span>
              </p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>

          <!-- Skip double book check -->
          <li>
            <div class="col value">
              <p class="bold">skipDoubleBookCheck</p>
              <p class="description">
                Boolean - If set to "true" will not check if this appointment is
                booking over another appointment (Only needs to be enabled if
                "bookedOnline" is set to "true")
              </p>
              <p class="examples">
                Examples:
                <span>true</span>
              </p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>

          <!-- Standing -->
          <li>
            <div class="col value">
              <p class="bold">repeat</p>
              <p class="description">
                Object - If set, will create a standing appointment with given
                repeat settings. Will set the "repeat.repeatId" field on each
                appointment you can use for the
                <span class="tag tag-blue">DELETE</span>
                <span :style="{ marginLeft: '8px' }" class="bold"
                  >/appointments/standing/:id</span
                >
                endpoint<br /><br />
                <span class="bold">repeats</span> - Boolean - If set to "true"
                will repeat appointment. <br />
                <span class="bold">amount</span> - Number - Amount of time to
                repeat appointment. (I.e. If this is set to 7 and "increment" is
                set to "days", appointment will repeat every 7 days) <br />
                <span class="bold">increment</span> - String - Time increment to
                repeat appointment. <br />
                <span class="bold">repeatTimes</span> - Number - Amount of times
                to repeat appointment. (Limit is 50) <br />
              </p>
              <p class="examples">
                Examples:
                <span
                  >{ repeats: true, amount: 7, increment: 'days', repeatTimes:
                  10 }</span
                >
              </p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>

          <!-- Start time text -->
          <li>
            <div class="col value">
              <p class="bold">startTimeText</p>
              <p class="description">
                String - Start time of appointment in text format. You must send
                this if you want to send the client an email or text
                confirmation.
              </p>
              <p class="examples">
                Examples:
                <span>January 1, 2021 12:00 PM CST</span>
              </p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div class="response">
      <h3>Response</h3>

      <!-- Body -->
      <div class="endpoint_body_section path">
        <div class="head">
          <h4>Body</h4>
        </div>

        <ul>
          <li>
            <div class="col value">
              <p class="bold">appointment</p>
              <p class="examples">
                Examples:
                <span
                  >{ "salonId": "622ec883ec2552e0d48ed226", "originalData": {
                  "services": [] }, "date": "2024-01-01T02:17:32.375Z",
                  "services": [ { "start": "16:00", "service": { "title": "Test
                  Item", "color": "#a6c7ea", "onBooking": false, "categoryId":
                  "622ec883ec2552e0d48ed275", "isCommissioned": false,
                  "commissionType": "percent", "commissionAmount": 0,
                  "priceType": "fixed", "displayByConsultationOnBooking": false,
                  "turns": 0, "price": 100, "taxRate": 0, "duration": 20,
                  "fees": { "supply": 0, "other": 0 }, "extraProcessingTime": 0,
                  "extraBlockedTime": 0, "hasPricingLevels": false,
                  "requireCardInfoToBook": false, "requiresCleaningLog": false,
                  "suggestedServices": [], "_id": "649c96eefe301442790a9e7c",
                  "levels": [] }, "duration": 20, "staff": { "staffId":
                  "622ec119ec2552e0d48ed156", "firstName": "John", "lastName":
                  "Doe", "email": "john@doe.com", "phoneNumber": "5551112222",
                  "commission": 60, "exemptFromServiceFees": false, "_id":
                  "649c96eefe301442790a9e7d", "customCommissions": [] },
                  "requested": false, "_id": "649c96eefe301442790a9e7b" } ],
                  "requestedCancellation": false, "reminderSent": false,
                  "clientConfirmationSent": false, "isBlockedTime": false,
                  "status": "new", "billing": { "noShowCharged": false,
                  "deposits": [] }, "repeat": { "repeats": false }, "files": {
                  "photos": [] }, "_id": "649c96eefe301442790a9e7a",
                  "created_at": "2023-06-28T20:24:14.807Z", "updated_at":
                  "2023-06-28T20:24:14.807Z", "__v": 0 }</span
                >
              </p>
            </div>

            <div class="col">
              <p class="required">required</p>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div class="reqResExamples">
      <h3>Examples</h3>

      <!-- Request -->
      <div class="endpoint_body_section path">
        <div class="head">
          <h4>Request</h4>
          <p>application/json</p>
        </div>

        <ul>
          <li>
            <p>
              { "salonId": "622ec883ec2552e0d48ed226", "date":
              "2024-01-01T02:17:32.375+00:00", "services": [ { "start": "14:00",
              "service": { "title": "Item", "color": "#a6c7ea",
              "isCommissioned": "false", "price": 100, "duration": 20,
              "categoryId": "622ec883ec2552e0d48ed212" }, "duration": "20",
              "staff": { "staffId": "622ec119ec2552e0d48ed192", "firstName":
              "John", "lastName": "Doe", "email": "john@doe.com", "phoneNumber":
              "5551112222", "commission": 60 }, "requested": false } ] }
            </p>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  computed: {
    apiUrl() {
      return process.env.VUE_APP_RASERVA_BACKEND;
    },
  },
};
</script>

<style lang="scss" scoped>
.endpoint {
  .tag {
    font-size: 12px;
    text-transform: uppercase;
    font-weight: 700;
    padding: 8px;
    border-radius: 5px;
    color: white;

    &-blue {
      background-color: var(--clr-secondary);
    }
  }

  &_head {
    h2 {
      font-size: 36px;
      margin-bottom: 16px;
    }

    .route {
      padding: 8px 16px;
      border-radius: 5px;
      background-color: var(--clr-white-2);
      display: flex;
      align-items: center;
      gap: 16px;

      .url {
        color: var(--clr-gray);

        span {
          color: var(--clr-primary);
          font-weight: 700;
        }
      }
    }

    .description {
      margin-top: 32px;
    }
  }

  &_body {
    .request,
    .response,
    .reqResExamples {
      margin-top: 32px;
      padding: 16px;
      border-radius: 5px;
      border: 1px solid var(--clr-light);
    }

    h3 {
      font-size: 24px;
      margin-bottom: 32px;
    }

    &_section {
      &:not(:first-child) {
        margin-top: 32px;
      }

      .head {
        display: flex;
        justify-content: space-between;

        h4 {
          font-size: 20px;
          margin-bottom: 16px;
          color: var(--clr-secondary);
        }

        p {
          font-size: 14px;
          color: var(--clr-gray);
        }
      }

      ul {
        .subitem {
          margin-left: 32px;
        }

        li {
          display: flex;
          justify-content: space-between;
          font-size: 14px;

          &:not(:first-child) {
            margin-top: 16px;
          }

          .value {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .bold {
            font-weight: 700;
          }
          .required {
            color: orangered;
          }

          .description {
            margin: 4px 0;
            color: var(--clr-gray);
          }

          .examples {
            span {
              background-color: var(--clr-white-2);
              padding: 4px 8px;
              border-radius: 5px;
              font-size: 12px;
            }
          }
        }
      }
    }
  }
}
</style>
